import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  INPUT_TYPE_LABEL,
  NO_QUEUE_FILTERS,
  STAGE_LABEL,
  activeQueueFilters,
  filterQueue,
  isStage,
  narrowsQueue,
  type InputType,
  type Kit,
  type QueueDueFilter,
  type QueueFilters,
  type QueueStatusFilter,
  type QueueTypeFilter,
  type Stage,
  dueKey,
} from '@/domain';
import { useSession } from '@/application/usecases/useSession';
import { useAssignedToMe, useWholeQueue } from '@/application/usecases/useKits';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { formatDuration, formatElapsed, useElapsed } from '@/application/timer/timerStore';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { strings } from '@/shared/strings';
import { labToday } from '@/shared/format/instant';
import { casePresets } from '@/ui/data/casePresets';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import { SortSelect } from '@/ui/data/SortSelect';
import { CaseMarks } from '@/ui/data/CaseMarks';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { ListFreshness } from '@/features/shell/ListFreshness';
import { useListMotion } from '@/ui/motion/useListMotion';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { FilterIcon, TrayIcon } from '@/ui/icons';
import styles from './QueueScreen.module.css';

/**
 * Which list is on screen. The queue is the cases assigned to this person; the
 * tray is their department's list, whoever holds each case. Section 1 of the
 * request of 5 October 2026 fixes the two words.
 */
type Which = 'queue' | 'tray';

const STATUS_OPTIONS: ReadonlyArray<{ value: QueueStatusFilter; label: string }> = [
  { value: 'any', label: strings.queue.anyStatus },
  { value: 'rework', label: strings.queue.rework },
  { value: 'held', label: strings.queue.held },
];

const TYPE_OPTIONS: ReadonlyArray<{ value: QueueTypeFilter; label: string }> = [
  { value: 'any', label: strings.queue.anyType },
  ...(Object.keys(INPUT_TYPE_LABEL) as InputType[]).map((key) => ({
    value: key,
    label: INPUT_TYPE_LABEL[key],
  })),
];

const DUE_OPTIONS: ReadonlyArray<{ value: QueueDueFilter; label: string }> = [
  { value: 'any', label: strings.queue.anyDue },
  { value: 'today', label: strings.queue.dueToday },
  { value: 'overdue', label: strings.queue.overdue },
];

/**
 * A technician's queue, and their department's tray. Section 5.2, redrawn for
 * the phone by the request of 4 October 2026, and given the tools to manage a
 * busy one by section 3 of the request of 5 October 2026: a search that runs as
 * they type, four sorts, four filters in a sheet from the bottom of the screen,
 * a count of what is left, and Reset.
 *
 * Newest first, unless they chose another order, which is remembered on the
 * device as every other list's is. The filters are not remembered: a queue
 * opened tomorrow with yesterday's filter still on hides the case that arrived
 * overnight.
 *
 * A technician does not claim work from a list. They either scan the QR on the
 * tray, or a manager assigns it. There is deliberately no claim button here:
 * it was explicitly removed, item 3 of section 17. Opening a kit is what starts
 * the timer, and the scan is the claim.
 */
export function QueueScreen() {
  const { data: session } = useSession();
  const [which, setWhich] = useState<Which>('queue');
  const [filters, setFilters] = useState<QueueFilters>(NO_QUEUE_FILTERS);
  const [sheetOpen, setSheetOpen] = useState(false);
  // The field shows every keystroke; the list follows when the typing pauses.
  const [typed, setTyped] = useState('');
  const search = useDebouncedValue(typed);

  const department = session?.user.department;
  const stage: Stage | undefined =
    department !== undefined && isStage(department) ? department : undefined;

  const tray = useWholeQueue(stage);
  const { data, isPending, isError } = tray;

  const me = session?.user.id ?? '';
  // What was handed to me at another department is in my queue too.
  const handed = useAssignedToMe(session?.user.id);

  // The same four orders, with the same meaning, as every other list of cases.
  const presets = useMemo(
    () => casePresets<Kit>(['newestFirst', 'oldestFirst', 'dueSoonest', 'urgentFirst']),
    [],
  );
  const order = useTableOrder('queue', presets);

  const trayItems = data?.items ?? [];
  const handedItems = handed.data?.items ?? [];
  const mine = [
    ...trayItems.filter((kit) => kit.assignedToUserId === me),
    ...handedItems.filter((kit) => !trayItems.some((own) => own.id === kit.id)),
  ];
  const listed = which === 'queue' ? mine : trayItems;

  const applied: QueueFilters = { ...filters, search };
  const today = labToday();
  const visible = order.apply(filterQueue(listed, applied, today));
  const narrowed = narrowsQueue({ ...filters, search: typed });
  const filterCount = activeQueueFilters(filters);

  function reset() {
    setFilters(NO_QUEUE_FILTERS);
    setTyped('');
  }

  const trayLabel =
    stage === undefined ? strings.queue.tray : strings.queue.trayOf(STAGE_LABEL[stage]);

  const sheetFilters: FilterDef[] = [
    {
      kind: 'toggle',
      label: strings.queue.urgentOnly,
      value: filters.urgentOnly,
      onChange: (urgentOnly) => setFilters((current) => ({ ...current, urgentOnly })),
    },
    {
      kind: 'select',
      label: strings.queue.status,
      value: filters.status,
      options: [...STATUS_OPTIONS],
      onChange: (status) =>
        setFilters((current) => ({ ...current, status: status as QueueStatusFilter })),
    },
    {
      kind: 'select',
      label: strings.queue.caseType,
      value: filters.caseType,
      options: [...TYPE_OPTIONS],
      onChange: (caseType) =>
        setFilters((current) => ({ ...current, caseType: caseType as QueueTypeFilter })),
    },
    {
      kind: 'select',
      label: strings.queue.due,
      value: filters.due,
      options: [...DUE_OPTIONS],
      onChange: (due) => setFilters((current) => ({ ...current, due: due as QueueDueFilter })),
    },
  ];

  return (
    <div className={styles.screen}>
      <section className={styles.station}>
        <span className={styles.stationIcon} aria-hidden="true">
          <TrayIcon />
        </span>
        <div className={styles.stationText}>
          <h2>{which === 'queue' ? strings.queue.myQueue : trayLabel}</h2>
          <p>{which === 'queue' ? strings.queue.queueHint : strings.queue.trayHint}</p>
        </div>
        {/* On a phone the refresh button sits in the app bar instead. */}
        <ListFreshness query={tray} />
      </section>

      <div className={styles.seg} role="group" aria-label={strings.queue.filterLabel}>
        <button
          type="button"
          className={which === 'queue' ? styles.on : undefined}
          aria-pressed={which === 'queue'}
          onClick={() => setWhich('queue')}
        >
          {strings.queue.myQueue}
        </button>
        <button
          type="button"
          className={which === 'tray' ? styles.on : undefined}
          aria-pressed={which === 'tray'}
          onClick={() => setWhich('tray')}
        >
          {trayLabel}
        </button>
      </div>

      {/*
        Search, the order, and the button that opens the filters. The shared
        filter bar, as on every other list; the filters themselves are in a
        sheet, so on a phone they cost one row here and open under the thumb.
      */}
      <div className={styles.tools}>
        <FilterBar
          label={strings.queue.toolsLabel}
          filters={[
            {
              kind: 'search',
              label: strings.queue.search,
              value: typed,
              onChange: setTyped,
              placeholder: strings.queue.searchPlaceholder,
            },
          ]}
          sort={<SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />}
          actions={
            <Button
              icon={<FilterIcon />}
              aria-haspopup="dialog"
              aria-expanded={sheetOpen}
              onClick={() => setSheetOpen(true)}
            >
              {strings.queue.filters}
              {filterCount > 0 && (
                <span className={styles.badge} data-testid="queue-filter-count">
                  {filterCount}
                </span>
              )}
            </Button>
          }
        />
      </div>

      {/*
        There is no single "your running task" banner any more.

        Section 2 of the request of 20 September 2026 allows several at once, so
        one banner could only ever be right about one of them. The running strip
        under the top bar lists them all with a Pause on each, on every screen, and
        each tray card below carries its own clock.
      */}

      {isPending && <SkeletonRows rows={5} label={strings.queue.loading} />}
      {isError && <p className={styles.hint}>{strings.errors.server}</p>}

      {!isPending && !isError && (
        <>
          <div className={styles.countRow} role="status">
            <span className={styles.countLabel}>{strings.queue.cases}</span>
            <span className={styles.count} data-testid="tray-count">
              {visible.length}
            </span>
            {narrowed && (
              <>
                <span className={styles.countOf}>
                  {strings.queue.ofTotal(listed.length)}
                </span>
                <button type="button" className={styles.reset} onClick={reset}>
                  {strings.queue.reset}
                </button>
              </>
            )}
          </div>
          {visible.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyIcon} aria-hidden="true">
                <TrayIcon />
              </span>
              {narrowed ? (
                <>
                  <h3>{strings.queue.noMatch}</h3>
                  <p>{strings.queue.noMatchDetail}</p>
                  <Button onClick={reset}>{strings.queue.reset}</Button>
                </>
              ) : which === 'queue' ? (
                <>
                  <h3>{strings.queue.queueEmpty}</h3>
                  <p>{strings.queue.queueEmptyDetail}</p>
                </>
              ) : (
                <>
                  <h3>{strings.queue.nothingElse}</h3>
                  <p>{strings.queue.nothingElseDetail}</p>
                </>
              )}
            </div>
          ) : (
            <TaskList kits={visible} me={me} department={stage} />
          )}
        </>
      )}

      {sheetOpen && (
        <BottomSheet
          title={strings.queue.filters}
          closeLabel={strings.sheet.close}
          onClose={() => setSheetOpen(false)}
          footer={
            <div className={styles.sheetFoot}>
              <Button onClick={() => setFilters(NO_QUEUE_FILTERS)} disabled={filterCount === 0}>
                {strings.queue.reset}
              </Button>
              <Button variant="primary" onClick={() => setSheetOpen(false)}>
                {strings.queue.show(filterQueue(listed, applied, today).length)}
              </Button>
            </div>
          }
        >
          <div className={styles.sheetFilters}>
            <FilterBar label={strings.queue.filters} filters={sheetFilters} />
          </div>
        </BottomSheet>
      )}
    </div>
  );
}

function TaskList({
  kits,
  me,
  department,
}: {
  kits: Kit[];
  me: string;
  department: Stage | undefined;
}) {
  // Rises in on first load, a new case arrives highlighted, a re-sorted one slides.
  const list = useListMotion<HTMLDivElement>(kits.map((kit) => kit.id));
  return (
    <div ref={list} className={styles.queue}>
      {kits.map((kit) => (
        <TaskRow key={kit.id} kit={kit} me={me} department={department} />
      ))}
    </div>
  );
}

function TaskRow({ kit, me, department }: { kit: Kit; me: string; department: Stage | undefined }) {
  const navigate = useNavigate();
  const mine = kit.assignedToUserId === me;
  // Red once the date has passed, amber on the day and the day before. The request of 7 October 2026, section 6.
  const due = kit.stage === 'ready' ? 'later' : dueKey(kit.deliveryDate, labToday());
  const dueTone = due === 'overdue' ? 'late' : due === 'later' ? '' : 'soon';
  /*
   * This card's own clock, when this case is one of the ones running. Section
   * 2.4: every running kit shows its clock on its own card, because a technician
   * with three going needs to see which of them has been running longest.
   */
  const elapsed = useElapsed(kit.id);

  const rowTone = kit.urgent
    ? styles.urgent
    : kit.rework
      ? styles.rework
      : due === 'overdue'
        ? styles.late
        : '';

  return (
    <button
      className={`${styles.task} ${rowTone}`}
      data-motion-key={kit.id}
      onClick={() => void navigate(`/queue/${kit.id}`)}
    >
      <span className={styles.stripe} />

      <span className={styles.jo}>
        <span className={styles.n}>
          {/* Urgent, a remake and on hold, as every case list marks them. */}
          <CaseMarks
            urgent={kit.urgent}
            redo={kit.redoOf != null}
            held={kit.held}
            urgentLabel={strings.queue.urgent}
          />
          {kit.jobOrderNo}
        </span>
        <span className={styles.p}>{kit.id}</span>
      </span>

      <span className={styles.tags}>
        {elapsed !== null && (
          <span className={`${styles.tag} ${styles.running}`}>
            <span className={styles.live} />
            {formatElapsed(elapsed)}
          </span>
        )}
        {/* Handed to me at another department: say where it is. */}
        {department !== undefined && kit.stage !== department && (
          <span className={`${styles.tag} ${styles.elsewhere}`}>{STAGE_LABEL[kit.stage]}</span>
        )}
        <span className={styles.tag}>{kit.product}</span>
        <span className={styles.tag}>
          {kit.teeth.length === 0
            ? `${kit.units} ${kit.units === 1 ? strings.queue.tooth : strings.queue.teeth}`
            : `${kit.teeth.length > 1 ? strings.queue.teeth : strings.queue.tooth} ${kit.teeth.join(', ')}`}
        </span>
        <span className={styles.tag}>
          {strings.queue.shade} {kit.shade}
        </span>
        <span className={styles.tag}>{INPUT_TYPE_LABEL[kit.input]}</span>
        {kit.noteCount > 0 && (
          <span className={`${styles.tag} ${styles.note}`}>{strings.queue.note}</span>
        )}
        {kit.urgent && (
          <span className={`${styles.tag} ${styles.urgent}`}>{strings.queue.urgent}</span>
        )}
        {kit.rework && (
          <span className={`${styles.tag} ${styles.rework}`}>{strings.queue.rework}</span>
        )}
        {kit.held && <span className={`${styles.tag} ${styles.held}`}>{strings.queue.held}</span>}
      </span>

      <span className={`${styles.due} ${dueTone === '' ? '' : (styles[dueTone] ?? '')}`}>
        {strings.queue.dueIn} <b>{strings.queue.days(kit.daysLeft)}</b> ·{' '}
        <span className={styles.waited}>
          {strings.queue.waited} {formatDuration(kit.minutesInStage * 60)}
        </span>
      </span>

      <span
        className={`${styles.assignee} ${
          mine ? styles.you : kit.assignedToUserId === null ? styles.free : styles.other
        }`}
      >
        {/* The name, never the id: an id on screen is the defect the client saw. */}
        {mine
          ? strings.queue.you
          : kit.assignedToUserId === null
            ? strings.queue.unassigned
            : (kit.assignedToUserName ?? strings.queue.assigned)}
      </span>
    </button>
  );
}
